import { useState } from "react";
import { Footprints, Trophy, Skull, Zap, PartyPopper, AlertTriangle, Award, Briefcase, Megaphone, TrendingUp, Sparkles, HeartPulse, Package } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ICONS = {
  Footprints, Trophy, Skull, Zap, PartyPopper, AlertTriangle,
  Award, Briefcase, Megaphone, TrendingUp, Sparkles, HeartPulse, Package,
};

function formatUnlockedAt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return null;
  }
}

export default function AchievementBadges({ achievements }) {
  const [openId, setOpenId] = useState(null);
  const [showAll, setShowAll] = useState(false);

  if (!achievements || achievements.length === 0) {
    return (
      <div className="text-xs text-hammerl-pencil font-mono italic px-3 py-2">
        Noch keine Auszeichnungen. Galoppiere härter.
      </div>
    );
  }

  // Sort by unlock date desc (newest first)
  const sorted = [...achievements].sort((a, b) => {
    const ta = a.unlocked_at || "";
    const tb = b.unlocked_at || "";
    return tb.localeCompare(ta);
  });
  const hidden = Math.max(0, sorted.length - 10);
  const visible = showAll || hidden === 0 ? sorted : sorted.slice(0, 10);

  return (
    <div className="p-3 space-y-2" data-testid="achievement-grid">
      <div className="grid grid-cols-4 gap-2">
        {visible.map((a) => {
        const Icon = ICONS[a.icon] || Award;
        const unlockedAt = formatUnlockedAt(a.unlocked_at);
        return (
          <Popover key={a.id} open={openId === a.id} onOpenChange={(v) => setOpenId(v ? a.id : null)}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="aspect-square bg-hammerl-card border-2 border-hammerl-ink flex items-center justify-center hover:bg-hammerl-highlight transition-colors shadow-brutal-sm focus:outline-none focus:bg-hammerl-highlight text-hammerl-paper"
                data-testid={`achievement-${a.id}`}
                aria-label={a.name}
              >
                <Icon className="w-5 h-5 text-hammerl-corpblue" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="center"
              className="w-72 rounded-none border-2 border-hammerl-ink bg-hammerl-card text-hammerl-ink shadow-brutal p-4"
              data-testid={`achievement-popover-${a.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-hammerl-highlight border-2 border-hammerl-ink flex items-center justify-center shrink-0 text-hammerl-paper">
                  <Icon className="w-5 h-5 text-hammerl-ink" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">Auszeichnung</div>
                  <div className="font-bold text-base leading-tight">{a.name}</div>
                </div>
              </div>
              <p className="mt-3 text-sm text-hammerl-ink leading-relaxed">{a.description}</p>
              {unlockedAt && (
                <div className="mt-3 pt-2 border-t border-hammerl-lines font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">
                  Freigeschaltet · {unlockedAt}
                </div>
              )}
            </PopoverContent>
          </Popover>
        );
      })}
      </div>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="w-full font-mono text-[10px] uppercase tracking-widest underline decoration-hammerl-ink underline-offset-2 hover:text-hammerl-corpblue transition-colors py-1"
          data-testid="toggle-achievements-btn"
        >
          {showAll ? `Nur letzte 10 zeigen` : `+${hidden} ältere anzeigen`}
        </button>
      )}
    </div>
  );
}
